import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '~/lib/utils';

const STORAGE_KEY = 'lumora-announcement-dismissed';

type AnnouncementValue = string | null | undefined;

declare global {
  interface Window {
    __lumoraAnnouncement?: AnnouncementValue;
  }
}

interface AnnouncementBannerProps {
  message?: AnnouncementValue;
  ctaLabel?: string;
  ctaHref?: string;
}

export function AnnouncementBanner({ message, ctaLabel, ctaHref }: AnnouncementBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [announcement, setAnnouncement] = useState<AnnouncementValue>(message);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'true') {
      return;
    }

    const globalAnnouncement = window.__lumoraAnnouncement ?? message;
    if (globalAnnouncement) {
      setAnnouncement(globalAnnouncement);
      setIsVisible(true);
    }
  }, [message]);

  const dismiss = () => {
    setIsVisible(false);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, 'true');
    }
  };

  if (!isVisible || !announcement) {
    return null;
  }

  return (
    <div className="border-b border-border/50 bg-gradient-to-r from-lumora-purple/15 via-transparent to-lumora-emerald/15">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-2 text-sm sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 text-foreground">
          <span className="rounded-md bg-lumora-purple/10 px-2 py-1 text-xs font-semibold text-lumora-purple">
            Update
          </span>
          <p className="text-sm text-muted-foreground">{announcement}</p>
        </div>
        <div className="flex items-center gap-3">
          {ctaLabel && ctaHref ? (
            <a
              href={ctaHref}
              className="rounded-md border border-lumora-purple/40 px-3 py-1 text-xs font-semibold text-lumora-purple transition hover:bg-lumora-purple/10"
            >
              {ctaLabel}
            </a>
          ) : null}
          <button
            type="button"
            onClick={dismiss}
            className={cn(
              'inline-flex items-center justify-center rounded-full border border-transparent p-1 text-muted-foreground transition hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-lumora-purple focus-visible:ring-offset-2'
            )}
            aria-label="Dismiss announcement"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}
